"use client";

import Image from "next/image";
import { useEffect } from "react";

import { StarPoint } from "@/app/(marketing)/components/demo/showcases/StarPoint";
import { Button } from "@/components/buttons/Button";
import { Modal } from "@/components/Modal";
import { Heading4 } from "@/components/text/Heading4";
import { TutorialOverlay } from "@/components/tutorial/TutorialOverlay";
import { MIN_CONVERSION_TIME_FOR_TUTORIAL } from "@/constants/tutorial";
import { useTutorial } from "@/lib/zustand/useTutorial/useTutorial";
import screenshot from "@/public/images/appScreenshots/artist-modal.png";
import { UserNoDates } from "@/types/defs/user";

export function StartTutorialButton({
  user,
  simplyUser,
}: {
  user: UserNoDates | null;
  simplyUser: boolean;
}) {
  const isActive = useTutorial((state) => state.isActive);
  const welcomeModalOpen = useTutorial((state) => state.welcomeModalOpen);
  const openWelcomeModal = useTutorial((state) => state.openWelcomeModal);
  const endWelcomeModalAndTutorial = useTutorial(
    (state) => state.endWelcomeModalAndTutorial
  );
  const startTutorial = useTutorial((state) => state.startTutorial);

  // Reset any stale tutorial state so we never show the overlay after back-then-forward.
  useEffect(() => {
    if (isActive && user !== null) {
      endWelcomeModalAndTutorial(user, simplyUser);
    }
  }, []);

  // If the user is not logged in, don't show the button (and the modal)
  if (user === null) {
    return null;
  }

  // Hide the tutorial button if user has less than 10 seconds of conversion time left
  const hasEnoughConversionTime =
    user.conversionTimeLeftSeconds === null ||
    user.conversionTimeLeftSeconds >= MIN_CONVERSION_TIME_FOR_TUTORIAL;

  if (!hasEnoughConversionTime) {
    return null;
  }

  return (
    <>
      <TutorialOverlay user={user} simplyUser={simplyUser} />
      <Button onClick={openWelcomeModal}>
        <span className="sm:hidden">Tutorial</span>
        <span className="hidden sm:inline">Start tutorial</span>
      </Button>
      <Modal
        open={welcomeModalOpen}
        closeModal={() => endWelcomeModalAndTutorial(user, simplyUser)}
        widthMode="wide"
        bgColor="primaryLight"
        centerTitle={true}
        title="Welcome to Audimee!"
      >
        <div className="flex flex-col items-center bg-primaryLight md:flex-row md:gap-x-8 lg:gap-x-16">
          <Image
            src={screenshot}
            alt="Audimee Tutorial"
            className="rotate-2 rounded-lg shadow-lg md:w-1/2 md:shadow-2xl"
            quality={100}
          />
          <div className="py-6 md:w-1/2">
            <Heading4 elementType="h2" className="mb-4">
              Here are a few things we can offer:
            </Heading4>

            <ul className="flex flex-col gap-y-2">
              <StarPoint>
                Turn any vocal into any of our +130 voice models across genres
                and styles
              </StarPoint>

              <StarPoint>
                Create harmonies and vocal stacks from a single vocal track
              </StarPoint>

              <StarPoint>Pitch correct your vocals</StarPoint>

              <StarPoint>
                Create a personalized voice model from your uploaded recordings
              </StarPoint>

              <StarPoint>
                Blend multiple voice models for a unique tone
              </StarPoint>
            </ul>

            <div className="mt-6 flex gap-4">
              <Button
                variant="ghost"
                onClick={() => endWelcomeModalAndTutorial(user, simplyUser)}
              >
                Skip
              </Button>
              <Button
                onClick={() => startTutorial(simplyUser)}
                className="bg-primary text-white hover:bg-primary/90"
              >
                Start tutorial
              </Button>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
}
